import type { Message } from '@crm/messenger';

export type ThreadEntry =
  | { readonly kind: 'divider'; readonly key: string; readonly text: string }
  | {
      readonly kind: 'message';
      readonly key: string;
      readonly message: Message;
      readonly title: string;
    };

export interface ThreadGroupingOptions {
  readonly now?: Date;
  readonly timeZone?: string;
  readonly gapMinutes?: number;
}

const DAY_MS = 86_400_000;
const NARROW_SPACES = /[\u202f\u00a0]/g;

/**
 * Interleaves iOS-style time dividers with the messages of a thread: a day
 * label plus local time ("Today 3:45 PM") on the first message of each local
 * day, and a bare time ("7:00 PM") after a long quiet gap within a day.
 */
export function buildThreadEntries(
  messages: readonly Message[],
  options: ThreadGroupingOptions = {},
): ThreadEntry[] {
  const { timeZone, gapMinutes = 60 } = options;
  const now = options.now ?? new Date();
  const dayKey = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' });
  const shortDate = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const fullDate = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const clean = (value: string) => value.replace(NARROW_SPACES, ' ');
  const dayNumber = (date: Date) => {
    const [year, month, day] = dayKey.format(date).split('-').map(Number);
    return Date.UTC(year, month - 1, day) / DAY_MS;
  };
  const today = dayNumber(now);
  const currentYear = Number(dayKey.format(now).slice(0, 4));

  const dayLabel = (date: Date) => {
    const age = today - dayNumber(date);
    if (age === 0) return 'Today';
    if (age === 1) return 'Yesterday';
    if (age > 1 && age < 7) return weekday.format(date);
    return Number(dayKey.format(date).slice(0, 4)) === currentYear
      ? shortDate.format(date)
      : fullDate.format(date);
  };

  const entries: ThreadEntry[] = [];
  let previous: Date | null = null;
  for (const message of messages) {
    const sent = new Date(message.sentAt);
    const valid = !Number.isNaN(sent.getTime());
    if (valid) {
      const newDay = !previous || dayKey.format(previous) !== dayKey.format(sent);
      const quiet =
        !!previous && sent.getTime() - previous.getTime() >= gapMinutes * 60_000;
      if (newDay || quiet) {
        const stamp = clean(time.format(sent));
        entries.push({
          kind: 'divider',
          key: `d-${message.id}`,
          text: newDay ? `${dayLabel(sent)} ${stamp}` : stamp,
        });
      }
      previous = sent;
    }
    entries.push({
      kind: 'message',
      key: message.id,
      message,
      title: valid ? `${clean(fullDate.format(sent))}, ${clean(time.format(sent))}` : '',
    });
  }
  return entries;
}
